import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { logout } from "../helpers/authHelper";
import AuthShowcase, { PasswordField, PasswordStrength, SubmitButton, TextField } from "../components/auth/AuthShowcase";

const Signup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      setError("Use at least one letter and one number in your password.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiRequest("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(), password: form.password })
      });
      // A new account never continues someone else's session in this browser.
      logout();
      const email = result.email || form.email.trim();
      navigate(`/verify-email?email=${encodeURIComponent(email)}`, { replace: true });
    } catch (requestError) {
      if (requestError.data?.code === "VERIFICATION_EMAIL_FAILED") {
        logout();
        navigate(`/verify-email?email=${encodeURIComponent(form.email.trim())}&delivery=failed`, { replace: true });
      } else {
        setError(requestError.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShowcase
      eyebrow="Set up your workspace in minutes"
      title={<>Turn your business profile into <em>a month of posts.</em></>}
      intro="Add your brand once. Socialyorix writes captions, hashtags and images in your voice, schedules them, and keeps every platform in sync."
    >
      <header className="auth-card-head">
        <h2>Create your account</h2>
        <p>Create your login, verify your email, then set up your business profile.</p>
      </header>

      <form className="auth-form" onSubmit={submit}>
        {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
        <div className="auth-row">
          <TextField id="first-name" name="firstName" label="First name" icon="ti-user" autoComplete="given-name" maxLength={80} placeholder="Asha" value={form.firstName} onChange={update} required />
          <TextField id="last-name" name="lastName" label="Last name" icon="ti-user" autoComplete="family-name" maxLength={80} placeholder="Sharma" value={form.lastName} onChange={update} required />
        </div>
        <TextField id="signup-email" name="email" label="Email address" icon="ti-mail" type="email" autoComplete="email" maxLength={254} placeholder="you@business.com" value={form.email} onChange={update} required />
        <div className="auth-field-group">
          <PasswordField id="signup-password" name="password" label="Password" autoComplete="new-password" placeholder="Create a password" describedBy="signup-password-rules"
            minLength={10} maxLength={72} pattern="(?=.*[A-Za-z])(?=.*[0-9]).{10,72}" title="Use 10 to 72 characters with at least one letter and one number."
            value={form.password} onChange={update} required />
          <PasswordStrength id="signup-password-rules" password={form.password} />
        </div>
        <PasswordField id="confirm-password" name="confirmPassword" label="Confirm password" autoComplete="new-password" placeholder="Repeat your password"
          minLength={10} maxLength={72} value={form.confirmPassword} onChange={update} required />
        <SubmitButton busy={submitting} busyLabel="Creating account…">Create account</SubmitButton>
      </form>

      <p className="auth-switch">Already have an account? <Link className="auth-link" to="/login">Sign in</Link></p>
    </AuthShowcase>
  );
};

export default Signup;
